import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import WalletService from "../../../../../../service/WalletService";
import localeText from "../../../../../../locale";
import { formatPrice } from "../../../../../../utilities/formatPrice";
import { formatDate } from "../../../../../../utilities/formatDate";
import "../FinancialTransactionsTable/FinancialTransactionsTable.css";

const walletService = new WalletService();

const typeFilterMap = {
  all: null,
  entry: "entry",
  outcome: "outcome",
};

const formatDateToISO = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseTransactions = (response) => {
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response)) return response;
  return [];
};

const mapLifetimeTransaction = (transaction) => {
  const income = Number(transaction?.income ?? 0) || 0;
  const outcome = Number(transaction?.outcome ?? 0) || 0;
  const isEntry = income > 0;
  const type = isEntry ? "entry" : "outcome";
  const value = isEntry ? income : outcome;
  const statusKey = transaction?.status === "COMPLETED" ? "completed" : "pending";

  return {
    id: transaction?.id,
    type,
    value,
    statusKey,
    date: transaction?.date,
  };
};

const LifeTimeWalletTable = ({ currencySymbol }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [showDateFilterDialog, setShowDateFilterDialog] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [draftStartDate, setDraftStartDate] = useState(null);
  const [draftEndDate, setDraftEndDate] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const token = localStorage.getItem("__extraconomy_token");
        const username = localStorage.getItem("__extraconomy_username");
        const dateBegin = formatDateToISO(startDate) || "1970-01-01";
        const dateEnd = formatDateToISO(endDate) || formatDateToISO(new Date());
        const response = await walletService.getLifetimeBonusTransactions(
          token,
          username,
          dateBegin,
          dateEnd,
        );
        if (cancelled) return;
        setRows(parseTransactions(response));
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const transactions = useMemo(() => rows.map(mapLifetimeTransaction), [rows]);

  const filteredTransactions = useMemo(() => {
    const type = typeFilterMap[filterType];
    if (!type) return transactions;
    return transactions.filter((transaction) => transaction.type === type);
  }, [transactions, filterType]);

  const statusConfig = {
    completed: { text: "completed", color: "var(--theme-primary, #1DAF61)" },
    pending: { text: "pending", color: "#F7B005" },
  };

  return (
    <div className="financial-transactions-table">
      <div className="flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div
          className="flex gap-1 flex-wrap p-1"
          style={{
            backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
            borderRadius: "0.625rem",
          }}
        >
          <Button
            size="small"
            label={localeText("all")}
            style={{ color: "#A3A3A3" }}
            className={`p-button-text border-round-pill font-medium ${filterType === "all" ? "filter-pill-active" : ""} p-no-outline`}
            onClick={() => setFilterType("all")}
          />
          <Button
            size="small"
            label={localeText("lifetimeWalletEntry")}
            style={{ color: "#A3A3A3" }}
            className={`p-button-text border-round-pill font-medium ${filterType === "entry" ? "filter-pill-active" : ""} p-no-outline`}
            onClick={() => setFilterType("entry")}
          />
          <Button
            size="small"
            label={localeText("lifetimeWalletOutcome")}
            style={{ color: "#A3A3A3" }}
            className={`p-button-text border-round-pill font-medium ${filterType === "outcome" ? "filter-pill-active" : ""} p-no-outline`}
            onClick={() => setFilterType("outcome")}
          />
        </div>
        <Button
          label={localeText("filter")}
          icon="pi pi-sliders-h"
          className="p-button-outlined border-round-lg p-no-outline"
          style={{
            color: "var(--theme-text-muted, #5C5C5C)",
            borderColor: "var(--theme-border, #EBEBEB)",
          }}
          onClick={() => {
            setDraftStartDate(startDate);
            setDraftEndDate(endDate);
            setShowDateFilterDialog(true);
          }}
        />
      </div>

      <Dialog
        header={localeText("filter") || "Filtrar"}
        visible={showDateFilterDialog}
        onHide={() => setShowDateFilterDialog(false)}
        className="w-25rem m-2"
        draggable={false}
      >
        <div className="flex flex-column gap-3">
          <div className="flex flex-column gap-1">
            <label>{localeText("startDate") || "Data inicial"}</label>
            <Calendar
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.value)}
              dateFormat="dd/mm/yy"
              showIcon
            />
          </div>
          <div className="flex flex-column gap-1">
            <label>{localeText("endDate") || "Data final"}</label>
            <Calendar
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.value)}
              dateFormat="dd/mm/yy"
              showIcon
            />
          </div>
          <div className="flex justify-content-end gap-2 mt-2">
            <Button
              type="button"
              className="p-button-text"
              style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
              label={localeText("clean") || "Limpar"}
              onClick={() => {
                setDraftStartDate(null);
                setDraftEndDate(null);
                setStartDate(null);
                setEndDate(null);
                setShowDateFilterDialog(false);
              }}
            />
            <Button
              type="button"
              label={localeText("applyChanges") || localeText("filter") || "Aplicar"}
              className="border-round-md p-no-outline"
              onClick={() => {
                setStartDate(draftStartDate);
                setEndDate(draftEndDate);
                setShowDateFilterDialog(false);
              }}
            />
          </div>
        </div>
      </Dialog>

      <div className="overflow-hidden">
        <DataTable
          value={filteredTransactions}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
          currentPageReportTemplate=""
          emptyMessage={
            <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
              <div
                className="border-round flex align-items-center justify-content-center"
                style={{
                  width: "120px",
                  height: "120px",
                  backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                }}
              >
                <i className="pi pi-inbox text-6xl text-400" />
              </div>
              <p className="m-0" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                {localeText("noOperationsRecords")}
              </p>
            </div>
          }
          className="financial-transactions-datatable"
        >
          <Column
            field="id"
            header={localeText("lifetimeWalletId")}
            body={(row) => <span className="font-medium">{row.id}</span>}
          />
          <Column
            field="type"
            header={localeText("operationType")}
            sortable
            body={(row) => (
              <span
                className="font-medium text-xs uppercase px-2 py-1 border-round-xl"
                style={{
                  color: row.type === "entry" ? "var(--theme-primary)" : "#FB3748",
                  backgroundColor:
                    row.type === "entry"
                      ? "color-mix(in srgb, var(--theme-primary) 10%, transparent)"
                      : "#FFEBEC",
                }}
              >
                {localeText(row.type === "entry" ? "lifetimeWalletEntry" : "lifetimeWalletOutcome")}
              </span>
            )}
          />
          <Column
            field="value"
            header={localeText("value")}
            sortable
            body={(row) => (
              <span className="font-medium">
                {row.type === "entry" ? "+" : "-"} {currencySymbol}{" "}
                {formatPrice(Math.abs(row.value))}
              </span>
            )}
          />
          <Column
            field="statusKey"
            header={localeText("status")}
            sortable
            body={(row) => {
              const cfg = statusConfig[row.statusKey] || statusConfig.pending;
              return (
                <span className="flex align-items-center gap-2">
                  <span
                    className="border-circle"
                    style={{
                      width: "8px",
                      height: "8px",
                      backgroundColor: cfg.color,
                    }}
                  />
                  {localeText(cfg.text)}
                </span>
              );
            }}
          />
          <Column
            field="date"
            header={localeText("date")}
            sortable
            body={(row) => formatDate(row.date)}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default LifeTimeWalletTable;
